import { useQuery } from '@apollo/client'
import TabContext from '@mui/lab/TabContext'
import Box from '@mui/material/Box'
import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useRegisterPageVisit } from 'recruiter/components/GlobalSearch/hooks/useRegisterPageVisit'
import { GetCandidateProfileForRecruiter } from 'recruiter/graphql/candidateProfile.gql'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getFullName } from 'shared/utils/users'
import { recruiterRouteConfig } from 'recruiter/routes'
import DocumentUpload from 'shared/components/DocumentUpload'
import ErrorBoundary from 'shared/components/ErrorBoundary'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import useToast from 'shared/components/Toast/useToast'
import { useNotesContext } from 'shared/utils/noteContext'
import Background from './Background'
import CandidateOverview from './CandidateOverview'
import Career from './Career'
import EditNotes from './EditNotes'
import EditProfile from './EditProfile'
import ProfileHeader from './ProfileHeader'
import { StyledTabPanel, TabsContainer, baseStyle } from './styled'

type CandidateProfileTabId =
    | 'overview'
    | 'career'
    | 'background'
    | 'communications'
    | 'documents'
export type CandidateRosterTableViewId = 'on' | 'all'

interface CandidateProfileBaseProps {
    initialTab: CandidateProfileTabId
    initialRosterTableView?: CandidateRosterTableViewId
}

interface CandidateProfileOverviewViewProps extends CandidateProfileBaseProps {
    initialTab: 'overview'
    initialRosterTableView: CandidateRosterTableViewId
}

interface CandidateProfileOtherViewProps extends CandidateProfileBaseProps {
    initialTab: Exclude<CandidateProfileTabId, 'overview'>
    initialRosterTableView?: undefined
}

type CandidateProfileProps =
    | CandidateProfileOverviewViewProps
    | CandidateProfileOtherViewProps

export function CandidateProfile({
    initialTab,
    initialRosterTableView = 'on',
}: CandidateProfileProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const { candidateId } = useParams()
    const [edit, setEdit] = useState(false)
    const toast = useToast()
    const { isEditingNote, selectedNote, closeEditNote } = useNotesContext()

    const {
        data: candidateProfileData,
        loading: isCandidateProfileDataLoading,
        refetch: refetchCandidateProfileData,
        error: candidateProfileError,
    } = useQuery(GetCandidateProfileForRecruiter, {
        variables: { candidateId },
        onError: (error) => {
            toast({
                title: 'Error',
                severity: 'error',
                message: error.message,
            })

            navigate(recruiterRouteConfig.candidates.path)
        },
    })

    const candidate = candidateProfileData?.candidate
    const candidateName = candidate?.user
        ? getFullName(candidate.user)
        : undefined
    const tabLabel = initialTab.charAt(0).toUpperCase() + initialTab.slice(1)
    usePageTitle(candidateName ? `${candidateName} · ${tabLabel}` : undefined)
    const currentWorkExperience = candidate?.workExperience?.find(
        (w) => w.isCurrent
    )
    useRegisterPageVisit({
        type: 'candidate',
        id: candidateId,
        name: candidate?.user ? getFullName(candidate.user) : undefined,
        subtitle: currentWorkExperience?.company?.value ?? undefined,
    })

    if (isCandidateProfileDataLoading) {
        return <LoadingPanel />
    }

    if (
        candidateProfileError ||
        !candidateProfileData ||
        !candidateProfileData.candidate
    ) {
        return <ErrorBoundary />
    }

    if (!candidateProfileData) {
        toast({
            title: 'Error',
            severity: 'error',
            message: 'Candidate not found',
        })

        navigate(recruiterRouteConfig.candidates.path)
        return
    }

    return (
        <Box>
            <EditProfile
                open={edit}
                onClose={() => setEdit(false)}
                data={candidateProfileData}
                refetch={refetchCandidateProfileData}
            />
            {(!selectedNote ||
                selectedNote.__typename === 'CandidateNoteWithType') && (
                <EditNotes
                    parentPath={location.pathname.substring(
                        0,
                        location.pathname.indexOf(candidateId ?? '')
                    )}
                    refetch={refetchCandidateProfileData}
                    open={isEditingNote}
                    note={selectedNote}
                    onClose={closeEditNote}
                    currentCandidate={candidateProfileData.candidate}
                />
            )}
            <TabContext value={initialTab}>
                <ProfileHeader
                    data={candidateProfileData}
                    setEdit={setEdit}
                    activeTab={initialTab}
                    candidateId={candidateId!}
                />
                <TabsContainer>
                    <StyledTabPanel value="overview">
                        <CandidateOverview
                            tableView={initialRosterTableView}
                            setTableView={() => {}}
                            data={candidateProfileData}
                            refetch={refetchCandidateProfileData}
                        />
                    </StyledTabPanel>

                    <StyledTabPanel value="career">
                        <Career />
                    </StyledTabPanel>

                    <StyledTabPanel value="background">
                        <Background
                            data={candidateProfileData}
                            refetch={refetchCandidateProfileData}
                        />
                    </StyledTabPanel>

                    <StyledTabPanel value="communications">
                        Communications
                    </StyledTabPanel>

                    <StyledTabPanel value="documents">
                        <DocumentUpload
                            data={candidateProfileData}
                            refetch={refetchCandidateProfileData}
                            isRecruiterPortal={true}
                            baseStyle={baseStyle}
                        />
                    </StyledTabPanel>
                </TabsContainer>
            </TabContext>
        </Box>
    )
}

export default CandidateProfile
